import { Component, OnInit, inject, computed } from '@angular/core';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { Router, RouterLink } from '@angular/router';
import { DOCUMENT, Location, UpperCasePipe } from '@angular/common';
import { TenantRoles } from '@sal/shared/domain/value-objects';
import { DesignService } from '../../services/design.service';
import { FileService } from '../../services/file-service';
import ElementDimHelper from '../../helper/element-dim.helper';
import { IUser } from '../../interfaces/IUser';
import {
    SiApplicationHeaderComponent,
    SiHeaderActionsDirective,
    SiHeaderBrandDirective,
    SiHeaderLogoDirective
} from '@siemens/element-ng/application-header';
import { SiHeaderDropdownComponent, SiHeaderDropdownTriggerDirective } from '@siemens/element-ng/header-dropdown';
import { SiAvatarComponent } from '@siemens/element-ng/avatar';
import { SiIconComponent } from '@siemens/element-ng/icon';
import { AuthService } from '../../../auth/services/auth.service';
import { TenantAuthorizationService } from '../../../auth/services/tenant-authorization.service';
import { EnergyBannerDialogService } from '../../services/energy-banner-dialog.service';

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    imports: [
        RouterLink,
        UpperCasePipe,
        TranslatePipe,
        SiApplicationHeaderComponent,
        SiHeaderActionsDirective,
        SiHeaderBrandDirective,
        SiHeaderLogoDirective,
        SiHeaderDropdownComponent,
        SiHeaderDropdownTriggerDirective,
        SiAvatarComponent,
        SiIconComponent
    ]
})
export class HeaderComponent implements OnInit {
    private readonly themeStorageKey = 'SAL_theme';
    private translate = inject(TranslateService);
    private documentRef = inject(DOCUMENT);
    private router = inject(Router);
    private location = inject(Location);
    private designService = inject(DesignService);
    private fileService = inject(FileService);
    private authService = inject(AuthService);
    private energyBannerDialogService = inject(EnergyBannerDialogService);
    protected readonly tenantAuthorizationService = inject(TenantAuthorizationService);

    languages = [
        { title: 'English', codeName: 'en' },
        { title: 'Deutsch', codeName: 'de' }
    ];

    public readonly helpMenuLinks = [
        { trasnlationString: 'Viva Engage', url: 'https://www.yammer.com/siemens.com' },
        { trasnlationString: 'SOC', url: 'https://soc.siemens.cloud/' },
        { trasnlationString: 'SCF', url: 'https://scf.siemens.cloud' },
        { trasnlationString: 'app.GSFS', url: 'app.GSFSLink' }
    ];
    public readonly logoUrl = computed(() => this.designService.designSet().logoUrl);
    public readonly salLogoUrl = computed(() => this.designService.designSet().salLogoUrl);
    public readonly infoFiles = computed(() => this.designService.designSet().infoFiles);

    public userNew: any;
    public navItems: any[];
    public navigationStyle: any = null;
    public route: string;
    public languageSelected = 'de';
    public userName = '';
    public department = '';
    public activeRoute = 'home';
    public infoBar = true;

    storage = null;
    currentlang = 'de';
    isDarkMode = false;

    public userPhotoUrl = computed(() => this.authService.userPhoto() ?? undefined);

    public get themeIcon(): string {
        return this.isDarkMode ? 'element-sun' : 'element-economy';
    }

    public get currentUser(): IUser | null {
        return this.authService.user();
    }

    public isEnergyTenant(): boolean {
        return (
            this.tenantAuthorizationService.activeTenantRole() === TenantRoles.SE ||
            this.tenantAuthorizationService.getRequiredTenantRole() === TenantRoles.SE
        );
    }

    public openEnergyDialog(): void {
        this.energyBannerDialogService.openDialog();
    }

    constructor() {
        this.currentlang = this.translate.getCurrentLang() || this.translate.getFallbackLang() || 'en';
        this.languageSelected = this.currentlang;
        this.router.events.subscribe(val => {
            if (this.location.path() !== '') {
                this.route = this.location.path();
            } else {
                this.route = 'Home';
            }
            setTimeout(() => {
                this._init();
            }, 400);
        });
    }

    public ngOnInit(): void {
        this.initializeTheme();

        const lang = localStorage.getItem('SAL_default_language');
        if (lang !== null) {
            this.languageSelected = lang;
        }
    }

    _init() {
        if (this.route === '/') {
            this.activeRoute = 'home';
        } else if (this.route.indexOf('news') !== -1) {
            this.activeRoute = 'news';
        }

        this.setActive(null, this.activeRoute);
    }

    public getNavStyle(selector: string) {
        let outputString = selector;
        if (selector === this.activeRoute) {
            outputString += ' ' + selector + '-active' + ' active-option';
        }
        return outputString;
    }
    public setActive(element: HTMLElement | null, selector: string): void {
        if (selector) {
            this.activeRoute = selector;
        }
        const navigationStyle = ElementDimHelper.getNavigationStyle('.' + this.activeRoute, true);
        if (navigationStyle) {
            this.navigationStyle = navigationStyle;
        }
    }

    public changeLanguage(lang: string) {
        localStorage.setItem('SAL_default_language', lang);
        window.location.reload();
    }

    public toggleTheme(): void {
        this.isDarkMode = !this.isDarkMode;
        this.applyTheme(this.isDarkMode);
        localStorage.setItem(this.themeStorageKey, this.isDarkMode ? 'dark' : 'light');
    }

    public get userInitials(): string {
        const first = this.currentUser?.firstname?.[0] ?? '';
        const last = this.currentUser?.surname?.[0] ?? '';
        return `${first}${last}`.toUpperCase();
    }

    private readDocument(key: string): Promise<string | false> {
        return this.fileService.getDocumentSignedUrl(key);
    }

    getFile(item: any): void {
        const key = this.currentlang === 'de' ? item.keyDE : item.keyEN;

        if (key) {
            this.readDocument(key).then((url: string | boolean) => {
                if (typeof url === 'string') {
                    window.open(url, '_blank');
                }
            });
        }
    }

    private initializeTheme(): void {
        const stored = localStorage.getItem(this.themeStorageKey);
        this.isDarkMode = stored === 'dark';
        this.applyTheme(this.isDarkMode);
    }

    private applyTheme(isDark: boolean): void {
        this.documentRef.documentElement.classList.toggle('app--dark', isDark);
        this.documentRef.body.classList.toggle('app--dark', isDark);
    }
}
